import * as THREE from "three";
import type { PhysicsBody } from "./physics";
import { Brush, Evaluator, INTERSECTION, SUBTRACTION } from "three-bvh-csg";

// ----------------------------------------------------------------------------
// Closed shell version of the cup. Real ceramic mugs are hollow — broken
// pieces are curved fragments of the wall, not chunks of solid ceramic.
// We trace the cup's full 2D cross-section as a closed polygon (outer
// wall up, across the rim, inner wall down, across the floor, down to
// the bottom, across the base back to start) and lathe it. The result
// is a watertight solid with a hollow cavity — exactly the geometry of
// a real cup wall + base. CSG-cut fragments will have an outer skin, an
// inner skin, and exposed cut faces.
// ----------------------------------------------------------------------------
const SHELL_GEOMETRY = createShellGeometry();

function createShellGeometry(): THREE.BufferGeometry {
  const points: THREE.Vector2[] = [];

  // Outer wall: base → rim. Mirrors createMugBodyGeometry from cup.ts so
  // the shell's outer surface lines up with the rendered cup exactly.
  points.push(new THREE.Vector2(0.45, 0.0));
  points.push(new THREE.Vector2(0.48, 0.05));
  points.push(new THREE.Vector2(0.5, 0.12));
  for (let i = 0; i <= 10; i++) {
    const t = i / 10;
    const y = 0.12 + t * 0.95;
    const r = 0.5 + Math.sin(t * Math.PI) * 0.02;
    points.push(new THREE.Vector2(r, y));
  }
  points.push(new THREE.Vector2(0.5, 1.1));
  points.push(new THREE.Vector2(0.49, 1.12));

  // Down across the top of the rim into the bevel — connects outer to inner.
  points.push(new THREE.Vector2(0.49, 1.1));
  points.push(new THREE.Vector2(0.47, 1.08));

  // Inner wall: rim → floor. Mirrors createInnerGeometry from cup.ts but
  // traversed in reverse.
  for (let i = 10; i >= 0; i--) {
    const t = i / 10;
    const y = 0.06 + t * 1.0;
    points.push(new THREE.Vector2(0.44, y));
  }

  // Across the cup's interior floor toward the central axis.
  points.push(new THREE.Vector2(0.42, 0.06));
  points.push(new THREE.Vector2(0.0, 0.06));

  // Down the central axis to the bottom of the solid base.
  points.push(new THREE.Vector2(0.0, 0.0));

  // Across the exterior base back to the starting point — closes the loop.
  points.push(new THREE.Vector2(0.45, 0.0));

  const lathe = new THREE.LatheGeometry(points, 48);
  lathe.computeVertexNormals();
  lathe.computeBoundingBox();
  return lathe;
}

// ----------------------------------------------------------------------------
// Half-space brush: a large box positioned so its volume occupies the
// half-space on the +planeNormal side of (planePoint, planeNormal). Used
// as the cutter in CSG: INTERSECT(piece, halfSpace) → piece above plane.
// ----------------------------------------------------------------------------
const HALF_SPACE_SIZE = 30;
const halfSpaceGeometry = new THREE.BoxGeometry(
  HALF_SPACE_SIZE,
  HALF_SPACE_SIZE,
  HALF_SPACE_SIZE,
);

function makeHalfSpaceBrush(
  planePoint: THREE.Vector3,
  planeNormal: THREE.Vector3,
): Brush {
  const brush = new Brush(halfSpaceGeometry);
  // BoxGeometry is centered. Place the box so its bottom face (-Y in box
  // local) lies on the plane, and the box extends along +planeNormal.
  brush.position
    .copy(planePoint)
    .addScaledVector(planeNormal, HALF_SPACE_SIZE / 2);
  brush.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), planeNormal);
  brush.updateMatrixWorld();
  return brush;
}

// ----------------------------------------------------------------------------
// Public API
// ----------------------------------------------------------------------------
export interface Fragment {
  mesh: THREE.Mesh;
  body: PhysicsBody;
}

export interface FractureInput {
  /**
   * World-space transform of the cup at the moment of fracture. Combines
   * the cup group's position + rotation.
   */
  worldMatrix: THREE.Matrix4;
  /** The cup's textured outer material. Reused for all fragments. */
  outerMaterial: THREE.Material;
  /**
   * Adds one shard body to the physics world and returns the handle.
   * The fracture engine generates geometry + hull points; the body
   * creation goes through physics.ts so we don't take a Rapier dep here.
   * Returns null when the convex hull was degenerate.
   */
  addShard: (
    worldCenter: { x: number; y: number; z: number },
    rotation: { x: number; y: number; z: number; w: number },
    hullPoints: Float32Array,
  ) => PhysicsBody | null;
  /** World-space contact point that triggered the break. */
  impactPoint: THREE.Vector3;
  /** Number of cutting planes (each doubles the chunk count). 3 → up to 8 fragments. */
  cuts?: number;
}

// ----------------------------------------------------------------------------
// Pre-computed fragment templates.
//
// CSG is the dominant cost in a fracture (~150-200ms for the 3-cut, 8-chunk
// case on a 2,900-tri shell). Since every cup uses the same SHELL_GEOMETRY,
// the cut topology only has to be computed once. We do it at module load:
// run the CSG flow in cup-local space against a canonical impact point, then
// store each chunk's geometry (centered at origin), local center, and the
// flat point array used to build the convex-hull collider.
//
// Runtime `fractureCup` then just clones the stored geometries, places them
// at the cup's current world pose, and creates rigid bodies — no CSG, no
// per-fragment hull point extraction. The result is visually identical
// across cups in their own local frame; their differing world poses at the
// moment of impact still randomize the look in world space.
// ----------------------------------------------------------------------------
interface FragmentTemplate {
  /** Origin-centered geometry in fragment-local space. */
  geometry: THREE.BufferGeometry;
  /** Bbox center in cup-local space — used to place the body at runtime. */
  localCenter: THREE.Vector3;
  /** Flat verts in fragment-local space — fed straight to ColliderDesc.convexHull. */
  hullPoints: Float32Array;
}

const evaluator = new Evaluator();
const FRAGMENT_TEMPLATES = computeFragmentTemplates(3);

function computeFragmentTemplates(cuts: number): FragmentTemplate[] {
  // Canonical impact: side-wall, mid-rim. Cup is symmetric around Y, so a
  // single side hit produces a representative shatter pattern.
  const localImpact = new THREE.Vector3(0.45, 0.7, 0);

  const sourceGeom = SHELL_GEOMETRY.clone();
  sourceGeom.computeVertexNormals();
  const placeholderMat = new THREE.MeshBasicMaterial();
  const sourceBrush = new Brush(sourceGeom, placeholderMat);
  sourceBrush.updateMatrixWorld();

  let chunks: Brush[] = [sourceBrush];
  for (let i = 0; i < cuts; i++) {
    const planeNormal = randomNormal();
    const halfSpace = makeHalfSpaceBrush(localImpact, planeNormal);
    const next: Brush[] = [];
    for (const chunk of chunks) {
      const above = evaluator.evaluate(chunk, halfSpace, INTERSECTION) as Brush;
      const below = evaluator.evaluate(chunk, halfSpace, SUBTRACTION) as Brush;
      if (chunkHasGeometry(above)) next.push(above);
      if (chunkHasGeometry(below)) next.push(below);
    }
    chunks = next;
    if (chunks.length === 0) break;
  }

  const templates: FragmentTemplate[] = [];
  for (const chunk of chunks) {
    const geom = chunk.geometry;
    geom.computeBoundingBox();
    const bb = geom.boundingBox!;
    const size = new THREE.Vector3();
    bb.getSize(size);
    if (size.x < 0.005 || size.y < 0.005 || size.z < 0.005) continue;

    const positions = geom.attributes.position;
    if (!positions || positions.count < 4) continue;

    const localCenter = new THREE.Vector3();
    bb.getCenter(localCenter);
    geom.translate(-localCenter.x, -localCenter.y, -localCenter.z);
    geom.computeVertexNormals();

    const pts = new Float32Array(positions.count * 3);
    for (let i = 0; i < positions.count; i++) {
      pts[i * 3] = positions.getX(i);
      pts[i * 3 + 1] = positions.getY(i);
      pts[i * 3 + 2] = positions.getZ(i);
    }

    templates.push({ geometry: geom, localCenter, hullPoints: pts });
  }

  return templates;
}

const tmpPos = new THREE.Vector3();
const tmpQuat = new THREE.Quaternion();
const tmpScale = new THREE.Vector3();

/**
 * Fracture the cup body into convex shards. Returns ready-to-add Three.js
 * meshes paired with Rapier rigid bodies that have already been added to
 * the world. Uses pre-computed templates — no runtime CSG.
 */
export function fractureCup(input: FractureInput): Fragment[] {
  const { worldMatrix, outerMaterial, addShard } = input;
  worldMatrix.decompose(tmpPos, tmpQuat, tmpScale);

  const fragments: Fragment[] = [];
  for (const tpl of FRAGMENT_TEMPLATES) {
    // Place each fragment in world space using the cup's pose. The hull
    // points stay in fragment-local; the body's pose puts them in world.
    const worldCenter = tpl.localCenter.clone().applyMatrix4(worldMatrix);

    const body = addShard(
      { x: worldCenter.x, y: worldCenter.y, z: worldCenter.z },
      { x: tmpQuat.x, y: tmpQuat.y, z: tmpQuat.z, w: tmpQuat.w },
      tpl.hullPoints,
    );
    if (!body) continue;

    const geom = tpl.geometry.clone();
    const mesh = new THREE.Mesh(geom, outerMaterial);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.position.copy(worldCenter);
    mesh.quaternion.copy(tmpQuat);
    fragments.push({ mesh, body });
  }
  return fragments;
}

function chunkHasGeometry(b: Brush): boolean {
  const pos = b.geometry.attributes.position;
  return !!pos && pos.count > 0;
}

function randomNormal(): THREE.Vector3 {
  return new THREE.Vector3(
    Math.random() - 0.5,
    Math.random() - 0.5,
    Math.random() - 0.5,
  ).normalize();
}
